import PIEList from '@src/shared/components/PIEList';
import React, { FC, useEffect, useState } from 'react';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import { ICourierSnap, IDeliveryStatus, IRoute } from '../../models/model-interface';
import styles from './DashboardCourierList.module.scss';
import DashboardCourierListItem from '../DashboardCourierListItem';
import { Scrollbars } from 'react-custom-scrollbars';
import PIEToggler from '@src/shared/components/PIEToggler';
import PIEText from '@src/shared/components/PIEText';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../../hooks/use-dashboard-store';
import DashboardEmptyCourierList from '../DashboardEmptyCourierList';
import useDeliveryDashboadAnalytics from '../../tracking/use-delivery-dashboard-analytics';
import DashboardUnavailableCourierList from '../DashboardUnavailableCourierList';
import DashboardStatisticsBannerMobile from '../DashboardStatisticsBannerMobile';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { DeliveryDashboardScreens } from '../../models/model-enum';
import { useOnboardingStore } from '@src/shared/store/onboarding/onboarding-store';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';

interface IDashboardCourierListProps {
  onCourierSelected?: (courier: ICourierSnap) => void;
}

export const DashboardCourierList: FC<IDashboardCourierListProps> = ({ onCourierSelected }) => {
  const { isResolutionMobile } = useDeviceDetect();
  const { activeCouriers, pendingCouriers, courierStatus, courierRoute, hasCourier } =
    useDashboardStore();
  const DEFAULT_ITEM_ROW_HEIGHT = 56;
  const { t } = useTranslation();
  const { trackCourierListView } = useDeliveryDashboadAnalytics();
  const { setActiveScreen } = useDashboardUIStore();
  const [togglerState, setTogglerState] = useState();
  const { onboardingActive, reminderActive } = useOnboardingStore();
  const { restaurant } = useKeycloakUserState();
  useEffect(() => {
    if (
      activeCouriers !== undefined &&
      pendingCouriers !== undefined &&
      !onboardingActive &&
      reminderActive
    ) {
      const totalCourierCount = activeCouriers.length + pendingCouriers.length;
      trackCourierListView(totalCourierCount)?.once();
    }
  }, [activeCouriers, pendingCouriers, onboardingActive]);

  return (
    <>
      {hasCourier ? (
        <>
          <PIEComponentUtil
            paddingTop="var(--xds-spacing-d)"
            paddingBottom="var(--xds-spacing-d)"
            className={styles.dashboardCourierList_items}
          >
            <Scrollbars
              autoHeightMin={0}
              autoHeightMax={400}
              autoHeight
              autoHide={false}
              autoHideTimeout={500}
              autoHideDuration={200}
              id="active-couriers-list"
            >
              <PIEList>
                {activeCouriers &&
                  activeCouriers?.map((courierItems: ICourierSnap, index: number) => {
                    return (
                      <DashboardCourierListItem
                        onClick={() => {
                          onCourierSelected && onCourierSelected(courierItems);
                        }}
                        courierStatus={
                          courierStatus &&
                          courierStatus.find(
                            (s: IDeliveryStatus) => s.courierId === courierItems.id
                          )?.currentDeliveryStatus
                        }
                        courierRoute={
                          courierRoute &&
                          (courierRoute.find(
                            (r: IRoute) => r.courier.id === courierItems.id
                          ) as IRoute)
                        }
                        key={index}
                        courierItem={courierItems as ICourierSnap}
                      />
                    );
                  })}
              </PIEList>
            </Scrollbars>
          </PIEComponentUtil>
          {!activeCouriers?.length && <DashboardUnavailableCourierList />}
          {isResolutionMobile && (
            <PIEComponentUtil marginBottom={'var(--xds-spacing-d)'}>
              <DashboardStatisticsBannerMobile
                onClick={() => {
                  setActiveScreen(DeliveryDashboardScreens.DELIVERY_DATA_SCREEN);
                }}
              />
            </PIEComponentUtil>
          )}
          <PIEComponentUtil
            paddingBottom="var(--xds-spacing-d)"
            className={styles.dashboardCourierList_items}
          >
            <PIEToggler
              isOpen={false}
              modPanelPadding={false}
              qaSelector="toggler-no-orders"
              isActive={true}
              onToggleClick={(data) => setTogglerState(data?.state)}
              label={
                <PIEText
                  text={t('dashboard.NO_AVAILABLE')}
                  size="var(--xds-font-size-16)"
                  align="left"
                  allowCaps={false}
                  fontWeight={`${
                    togglerState ? 'var(--xds-font-weight-bold)' : 'var(--xds-font-weight-regular)'
                  }`}
                />
              }
              onOpen={({ refs }) => {
                const { PIETogglerHeaderContainerRef } = refs;
                PIETogglerHeaderContainerRef.current.style.borderBottom = 'unset';
              }}
              onCloseAnimationEnd={({ refs }) => {
                const { PIETogglerHeaderContainerRef } = refs;
                PIETogglerHeaderContainerRef.current.style.borderBottom = '';
              }}
            >
              <Scrollbars
                autoHeightMin={isResolutionMobile ? 'max-content' : 0}
                autoHeightMax={isResolutionMobile ? 'max-content' : 170}
                autoHeight
                autoHide
                autoHideTimeout={500}
                autoHideDuration={200}
              >
                <PIEList qaSelector="list-nested">
                  {pendingCouriers &&
                    pendingCouriers
                      .filter((c: ICourierSnap) => !c.isGuest)
                      .map((courierItems: ICourierSnap, index: number) => {
                        return (
                          <DashboardCourierListItem
                            key={index}
                            isCourierActive={false}
                            courierItem={courierItems as ICourierSnap}
                            onClick={() => {
                              onCourierSelected && onCourierSelected(courierItems);
                            }}
                          />
                        );
                      })}
                </PIEList>
              </Scrollbars>
            </PIEToggler>
          </PIEComponentUtil>
        </>
      ) : (
        <>
          <DashboardEmptyCourierList />
        </>
      )}
    </>
  );
};

export default DashboardCourierList;
